import Icon from '@atoms/Icon/Icon';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IMeta } from '@models/IMeta';
import { ISubheaderCards } from '@models/ISubheaderCards';
import AccordionItem from '@molecules/AccordionItem/AccordionItem';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import SubheaderCard from '@molecules/SubheaderCard/SubheaderCard';
import useProductPageContentStore from '@state/ProductPageStore';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const SubheaderCards = (props: ISubheaderCards) => {
  const { tabs, meta } =
    useProductPageContentStore().productPageContent?.subheaderStatementCards ||
    props;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [openAccordionItems, setOpenAccordionItems] = useState<number[]>([]);
  const isMobile = useMediaQuery(1024);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    setActiveTabIndex(0);
    setOpenAccordionItems([]);
  }, [isMobile]);

  const handleTabChange = (tabTitle: string) => {
    updateDataLayer({
      event: 'selectContent',
      content_type: 'Subheader cards',
      link_text: tabTitle,
    });
  };

  if (!tabs?.length) {
    return null;
  }

  return (
    <section
      className="component-padding container bg-white text-black-100"
      data-testid="CT-160-Subheader-Cards"
      data-epi-block-id={meta?.contentLink}
    >
      <SoftLoadWrapper>
        <div className="lg:hidden">
          {tabs.map(({ tabIcon, tabName, meta: tabMeta, cards }, tabIndex) => {
            const isAccordionOpen = openAccordionItems.includes(tabIndex);

            return (
              <AccordionItem
                activeIndex={isAccordionOpen ? tabIndex : -1}
                key={`accordion-item-${tabName}`}
                index={tabIndex}
                isActive={isAccordionOpen}
                meta={tabMeta}
                title={tabName}
                icon={tabIcon}
                dataTestId={`CT-160-Subheader-Tab-${tabIndex}`}
                onClick={() => {
                  const newOpenAccordionItems = isAccordionOpen
                    ? openAccordionItems.filter((index) => index !== tabIndex)
                    : [...openAccordionItems, tabIndex];
                  setOpenAccordionItems(newOpenAccordionItems);
                  handleTabChange(tabName);
                }}
              >
                <div
                  className="flex flex-col gap-10"
                  data-epi-property-name={meta?.editHints?.cards}
                >
                  {cards.map((card, cardIndex) => (
                    <SubheaderCard
                      {...card}
                      index={cardIndex}
                      key={`${tabName}-${card.subheader}-${card.body}`}
                    />
                  ))}
                </div>
              </AccordionItem>
            );
          })}
        </div>

        <div className="max-lg:hidden">
          {tabs.length > 1 && (
            <ShadowContainerHorizontal
              threshold={0.01}
              className="mb-20"
              hasTabbableNestedItems
            >
              <div className="flex gap-x-5">
                {tabs.map(({ tabName, tabIcon }, index) => {
                  const isActiveTab = activeTabIndex === index;

                  return (
                    <button
                      className={classNames(
                        'flex items-center gap-3 rounded-[100px] px-5 py-2',
                        isActiveTab
                          ? 'pointer-events-none bg-black-100 text-white'
                          : 'bg-grey-200 hover:bg-grey'
                      )}
                      disabled={isActiveTab}
                      key={`tab-${tabName}`}
                      onClick={() => {
                        setActiveTabIndex(index);
                        handleTabChange(tabName);
                      }}
                      data-testid={`CT-160-Subheader-Tab-${index}`}
                    >
                      {tabIcon && (
                        <Icon
                          name={tabIcon}
                          size="1.25rem"
                          colorClassName={
                            isActiveTab ? 'text-white' : 'text-black-100'
                          }
                          data-testid={`CT-160-Subheader-Tab-${index}-Icon`}
                        />
                      )}

                      <p className="body-one">{tabName}</p>
                    </button>
                  );
                })}
              </div>
            </ShadowContainerHorizontal>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            key={tabs[activeTabIndex]?.tabName}
          >
            <div
              className="grid grid-cols-2 gap-10 lg:gap-x-24 lg:gap-y-[100px] xl:grid-cols-4"
              data-epi-property-name={meta?.editHints?.cards}
            >
              {tabs[activeTabIndex]?.cards.map((card, index) => (
                <SubheaderCard
                  {...card}
                  index={index}
                  key={`${card.subheader}-${card.body}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default SubheaderCards;
